import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Pencil2Icon } from '@radix-ui/react-icons';
import styles from './BacktestPage.module.css';
import Button from '../../components/common/Button/Button';
import { useStrategies } from '../../hooks/useStrategies';
import { formatRange } from './backtestHelpers';
import BacktestResults from './BacktestResults';

/**
 * Backtest page — full-width replacement for the old BacktestModal.
 *
 * Why a page (not a modal):
 *   - the indicator-columns set grows over time, so the matches
 *     table needs real horizontal room + sticky header / first
 *     column. A modal can never give that.
 *
 * Behaviour kept identical to the old modal:
 *   - same default dates (last 7 days → today)
 *   - same client-side validation (start < end; no max range cap)
 *   - same backtestStrategy(id, start, end) call
 *   - same results shape: { summary, matches, indicatorColumns }
 *
 * Added on top:
 *   - skeleton placeholders while the strategy is loading AND
 *     while a backtest is running, so the user always sees the
 *     final shape of the screen instead of a blank flash
 *   - error card with a Retry button — re-runs the same dates
 *     without making the user re-pick them
 */
const BacktestPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state;
  const {
    selectedStrategy,
    loading: strategyLoading,
    fetchStrategyById,
    backtestStrategy,
  } = useStrategies();

  // ── Date pickers ────────────────────────────────────────
  // Default: last 7 days. If the user arrives from the Strategy
  // Detail page via navigate-with-state, use those dates instead
  // so the backtest runs with the range they already picked.
  const [startDate, setStartDate] = useState(() => {
    if (navState?.startDate) return navState.startDate;
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(
    () => navState?.endDate || new Date().toISOString().split('T')[0]
  );

  // ── Run state ───────────────────────────────────────────
  // `running` is local so the page-level loading state is
  // independent of the hook's loading flag (which is shared
  // across all strategy actions).
  const [results, setResults] = useState(null);
  const [runError, setRunError] = useState('');
  const [running, setRunning] = useState(false);

  // When the user opens the date card back up after a successful
  // run (via the pencil-edit button on the header pill), we flip
  // this on. It's cleared again the next time they hit Run, so a
  // successful re-run collapses the card on its own.
  const [editingDates, setEditingDates] = useState(false);

  // The single symbol to backtest. A strategy can own up to 20
  // symbols but the BacktestService is per-symbol, so the user
  // picks one here. Initialised once the strategy loads — see the
  // init effect below.
  const [selectedSymbol, setSelectedSymbol] = useState(null);

  // Holds the nav-state symbol until availableSymbols loads so
  // Effect 2 can validate it. A ref (not state) because we don't
  // want a re-render when we consume it — Effect 2 reads and
  // clears it in the same tick.
  const pendingNavSymbol = useRef(navState?.symbol || null);

  // Available symbols for the picker. Prefer the multi-symbol
  // array from the new backend response; fall back to wrapping
  // the legacy single `symbol` string.
  //
  // Declared up here (not down with the other derived values)
  // because the init effect below references it via its deps
  // array, which React reads synchronously during render. If
  // this useMemo lived after the early-return block, accessing
  // it from a top-of-component effect would hit a TDZ.
  const availableSymbols = useMemo(() => {
    if (!selectedStrategy) return [];
    if (Array.isArray(selectedStrategy.symbols) && selectedStrategy.symbols.length > 0) {
      return selectedStrategy.symbols;
    }
    return selectedStrategy.symbol ? [selectedStrategy.symbol] : [];
  }, [selectedStrategy]);

  // Fetch the parent strategy once on mount so we can show its
  // name/symbol/timeframes in the header.
  useEffect(() => {
    if (id) fetchStrategyById(id).catch(() => {});
  }, [id, fetchStrategyById]);

  // Initialise / reconcile the selected symbol once the strategy
  // loads. We also defensively re-pick if the user's selection
  // disappears from the strategy mid-session (e.g. the strategy
  // was edited in another tab to drop that symbol). Defaults to
  // the first symbol so the picker is never empty when there's
  // at least one option.
  //
  // When navigating from the Strategy Detail page, a nav-state
  // symbol is held in `pendingNavSymbol` ref. We DON'T clear the
  // symbol while the ref is pending (strategy still loading) and
  // we validate + apply it once availableSymbols arrives.
  useEffect(() => {
    if (availableSymbols.length === 0) {
      // Don't clear symbol if a nav-state symbol is pending validation
      if (!pendingNavSymbol.current && selectedSymbol !== null) {
        setSelectedSymbol(null);
      }
      return;
    }
    // Consume pending nav symbol: validate, then clear the ref
    if (pendingNavSymbol.current) {
      const navSym = pendingNavSymbol.current;
      pendingNavSymbol.current = null;
      if (availableSymbols.includes(navSym)) {
        setSelectedSymbol(navSym);
        return;
      }
      // Nav symbol invalid (strategy edited?) — fall through to default
    }
    if (!selectedSymbol || !availableSymbols.includes(selectedSymbol)) {
      setSelectedSymbol(availableSymbols[0]);
    }
  }, [availableSymbols, selectedSymbol]);

  // Esc key closes the edit-mode date card — matches Stripe /
  // Linear / Notion behaviour for configuration overlays. Only
  // active while the card is open in edit mode (and not running),
  // so it never fires during fresh visits or in-flight requests.
  useEffect(() => {
    if (!editingDates || !results || running) return;
    const handler = (e) => {
      if (e.key === 'Escape') setEditingDates(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editingDates, results, running]);

  // Basic client-side validation — both dates required and start
  // on/before end. No maximum range cap (any span is allowed).
  const dateError = useMemo(() => {
    if (!startDate || !endDate) return 'Both dates are required';
    if (new Date(startDate) > new Date(endDate))
      return 'Start must be on or before end';
    return '';
  }, [startDate, endDate]);

  // The actual run handler — reused by the Run button AND the
  // Retry button so they behave identically.
  const handleRun = useCallback(async () => {
    if (dateError) return;
    // No symbol = nothing to backtest. We disable the Run button
    // in this state too, so this is a belt-and-braces guard.
    if (!selectedSymbol) return;
    setRunError('');
    setResults(null);
    setRunning(true);
    // Close the editing affordance — if the run succeeds, the
    // date card stays collapsed. If it fails, the next render
    // re-shows the card because `results` is still null and the
    // user needs to be able to adjust before retrying.
    setEditingDates(false);
    try {
      const res = await backtestStrategy(id, startDate, endDate, selectedSymbol);
      // Response interceptor already unwraps response.data, but
      // the hook layer may pass either shape — handle both so
      // we don't depend on internal axios behaviour.
      setResults(res?.data || res);
    } catch (err) {
      setRunError(err?.message || 'Backtest failed. Please try again.');
    } finally {
      setRunning(false);
    }
  }, [id, startDate, endDate, backtestStrategy, dateError, selectedSymbol]);

  // ── Auto-run from Strategy Detail navigation ────────────
  // When the user clicks "Run Backtest" on the Strategy Detail
  // page, we arrive here with navState.autoRun === true. This
  // effect waits until the strategy is loaded, the symbol is
  // resolved, and dates are valid — then fires handleRun once.
  // Five independent guards prevent premature or duplicate runs.
  const autoRanRef = useRef(false);

  useEffect(() => {
    if (autoRanRef.current) return;          // already ran
    if (!navState?.autoRun) return;          // not an auto-run navigation
    if (!selectedStrategy) return;           // strategy not loaded yet
    if (!selectedSymbol) return;             // symbol not resolved yet
    if (dateError) return;                   // invalid dates

    autoRanRef.current = true;
    // Clear nav state so a browser refresh doesn't re-trigger.
    // replaceState does NOT fire popstate — React Router won't
    // re-render. The stale navState in React is harmless because
    // autoRanRef blocks any subsequent runs.
    window.history.replaceState({}, '', window.location.pathname);
    handleRun();
  }, [navState, selectedStrategy, selectedSymbol, dateError, handleRun]);

  // ── Redirect on refresh / direct URL access ─────────────
  // The backtest page is a transient results view — it depends
  // on navigation state passed from StrategyDetail. If that
  // state is missing (browser refresh, direct URL, bookmark),
  // redirect back to the strategy detail page so the user can
  // re-launch the backtest from a valid starting point.
  useEffect(() => {
    if (!location.state) {
      navigate(`/strategies/${id}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived display data ────────────────────────────────
  // Conditions come back as an array OR a JSONB string depending
  // on the path that loaded the strategy. Same defensive parse
  // the old modal used so timeframe badges always render.
  const strategy = selectedStrategy;
  const conditions = Array.isArray(strategy?.conditions)
    ? strategy.conditions
    : typeof strategy?.conditions === 'string'
      ? (() => {
          try {
            return JSON.parse(strategy.conditions || '[]');
          } catch {
            return [];
          }
        })()
      : [];

  // Collect every timeframe mentioned anywhere in the conditions
  // (variable side + value side when comparing two indicators).
  const timeframes = [
    ...new Set(
      conditions.flatMap((c) => [
        c.variableTimeframe || c.timeframe || '1m',
        ...(c.valueType === 'IndicatorValue'
          ? [c.valueTimeframe || c.timeframe || '1m']
          : []),
      ])
    ),
  ];

  // ── Layout state ────────────────────────────────────────
  // The date card and the range pill are mutually exclusive:
  //   - before any successful run → card visible, no pill
  //   - while running / on error  → card visible, no pill
  //     (so the user can adjust dates and retry inline)
  //   - on success                → card collapses, pill shows
  //   - user clicks pencil on pill → card re-opens, pill hides
  // The configCard should be hidden when:
  //   - a backtest is currently running
  //   - auto-run is pending (navigated from detail page, about to run)
  //   - strategy is still loading
  // Otherwise: show when no results yet OR user clicked the edit pencil.
  const autoRunPending = navState?.autoRun && !autoRanRef.current;
  const showDateCard = !running && !autoRunPending && (!results || editingDates);
  const showRangePill = !!results && !editingDates;
  // Close affordance on the date card is only meaningful when
  // the user opened the card via the pencil — in any other state
  // (first visit, running, error) there's nothing to cancel back
  // to and the X would either be misleading or strand them.
  const showCloseBtn = !!results && editingDates && !running;

  // ── Guard: no nav state → redirecting ───────────────────
  // Prevents the configCard / skeleton from flashing while
  // the redirect effect above is firing.
  if (!navState && !results) {
    return null;
  }

  // ── Render: initial-load skeleton ───────────────────────
  // We render this *only* before the strategy is in hand. Once
  // we have it, even a refetch leaves the real header in place
  // so the page never re-flashes.
  if (strategyLoading && !strategy) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button
              className={styles.backBtn}
              onClick={() => navigate(`/strategies/${id}`)}
            >
              <span className="material-symbols-outlined">arrow_back</span>
              Back
            </button>
            <div className={styles.titleBlock}>
              <div className={`${styles.skel} ${styles.skelTitle}`} />
              <div className={styles.skelBadges}>
                <div className={`${styles.skel} ${styles.skelBadge}`} />
                <div className={`${styles.skel} ${styles.skelBadge}`} />
                <div className={`${styles.skel} ${styles.skelBadge}`} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: strategy doesn't exist / 404 ────────────────
  if (!strategy) {
    return (
      <div className={styles.page}>
        <div className={styles.notFound}>
          <span
            className="material-symbols-outlined"
            style={{ fontSize: 48, color: 'var(--text-muted)' }}
          >
            search_off
          </span>
          <h3>Strategy not found</h3>
          <Button variant="outline" onClick={() => navigate('/strategies')}>
            Back to Strategies
          </Button>
        </div>
      </div>
    );
  }

  // ── Render: normal page ────────────────────────────────
  return (
    <div className={styles.page}>
      {/* Header — title + badges on left, pills on right, one line */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            className={styles.backBtn}
            onClick={() => navigate(`/strategies/${id}`)}
          >
            <span className="material-symbols-outlined">arrow_back</span>
            Back
          </button>
          <div className={styles.titleBlock}>
            <h1 className={styles.title}>Backtest — {strategy.name}</h1>
            <div className={styles.badges}>
              {/* {timeframes.map((tf) => (
                <span key={tf} className={styles.badge}>
                  {tf}
                </span>
              ))} */}
              <span
                className={`${styles.badge} ${
                  strategy.action === 'BUY' ? styles.buyBadge : styles.sellBadge
                }`}
              >
                {strategy.action}
              </span>
            </div>
          </div>
        </div>

        {showRangePill && (
          <div className={styles.headerRight}>
            <div className={styles.pillGroup}>
              {/* Symbol pill — shows which symbol the visible
                  results belong to. Only meaningful once a run has
                  completed, hence inside `showRangePill`. */}
              {selectedSymbol && (
                <div className={styles.symbolPill}>
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    trending_up
                  </span>
                  <span className={styles.symbolPillText}>{selectedSymbol}</span>
                </div>
              )}
              <div className={styles.rangePill}>
                <span
                  className="material-symbols-outlined"
                  aria-hidden="true"
                >
                  date_range
                </span>
                <span className={styles.rangeText}>
                  {formatRange(startDate, endDate)}
                </span>
                {/* One edit button for the whole "what was run"
                    context — opens the config card with both the
                    symbol picker and date pickers re-editable. */}
                <button
                  type="button"
                  className={styles.rangeEditBtn}
                  onClick={() => setEditingDates(true)}
                  aria-label="Edit backtest configuration"
                  title="Edit symbol & date range"
                >
                  <Pencil2Icon
                    className={styles.rangeEditIcon}
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Config card — only shown when the user needs to interact
          with the date range. On success it collapses into the
          header pill above; the pill's pencil button re-opens it. */}
      {showDateCard && (
        <div className={styles.configCard}>
          {showCloseBtn && (
            <button
              type="button"
              className={styles.cardCloseBtn}
              onClick={() => setEditingDates(false)}
              aria-label="Cancel editing date range"
              title="Cancel (Esc)"
            >
              <span
                className="material-symbols-outlined"
                aria-hidden="true"
              >
                close
              </span>
            </button>
          )}

          <div className={styles.dateRow}>
            {/* Symbol picker. Three states tied to availability:
                0 symbols → not rendered (Run is disabled below);
                1 symbol → static text (no real choice = no widget);
                2+ symbols → native select dropdown. */}
            {availableSymbols.length > 0 && (
              <div className={styles.symbolField}>
                <label className={styles.label} htmlFor="backtest-symbol">Symbol</label>
                {availableSymbols.length > 1 ? (
                  <select
                    id="backtest-symbol"
                    className={styles.select}
                    value={selectedSymbol || ''}
                    disabled={running}
                    onChange={(e) => setSelectedSymbol(e.target.value)}
                  >
                    {availableSymbols.map((sym) => (
                      <option key={sym} value={sym}>{sym}</option>
                    ))}
                  </select>
                ) : (
                  <div className={styles.symbolStaticText}>{selectedSymbol || '—'}</div>
                )}
              </div>
            )}
            <div className={styles.dateField}>
              <label className={styles.label}>Start Date</label>
              <input
                type="date"
                className={styles.input}
                value={startDate}
                max={endDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={running}
              />
            </div>
            <div className={styles.dateField}>
              <label className={styles.label}>End Date</label>
              <input
                type="date"
                className={styles.input}
                value={endDate}
                min={startDate}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={running}
              />
            </div>
            <div className={styles.runField}>
              <Button
                variant="primary"
                onClick={handleRun}
                loading={running}
                disabled={!!dateError || running || !selectedSymbol}
              >
                Run Backtest
              </Button>
            </div>
          </div>
          {dateError && <p className={styles.errorText}>{dateError}</p>}
          {availableSymbols.length === 0 && (
            <p className={styles.errorText}>
              This strategy has no symbols configured. Add at least one symbol before backtesting.
            </p>
          )}

        </div>
      )}

      {/* Results area — four mutually exclusive states. The order
          here is the priority order: running > error > results >
          idle. */}
      {running ? (
        // ── Skeleton: shows the final shape of the results block
        // so the page doesn't jump when real data lands. ────────
        <div className={styles.skelResultsCard}>
          <div className={styles.skelSummaryGrid}>
            <div className={`${styles.skelSummaryCard} ${styles.skel}`} />
            <div className={`${styles.skelSummaryCard} ${styles.skel}`} />
            <div className={`${styles.skelSummaryCard} ${styles.skel}`} />
          </div>
          <div className={styles.tableSkel}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={`${styles.skel} ${styles.skelRow}`} />
            ))}
          </div>
        </div>
      ) : runError ? (
        // ── Error card with retry — same dates, just re-runs. ──
        <div className={styles.errorCard}>
          <span
            className="material-symbols-outlined"
            style={{ fontSize: 40, color: 'var(--error-red)' }}
          >
            error
          </span>
          <h3 className={styles.errorTitle}>Backtest failed</h3>
          <p className={styles.errorMessage}>{runError}</p>
          <Button variant="primary" onClick={handleRun}>
            Retry
          </Button>
        </div>
      ) : results ? (
        // ── Results: delegated to BacktestResults component. ──
        <BacktestResults results={results} />
      ) : null}
    </div>
  );
};

export default BacktestPage;
